import { Flex, Icon, Text, useBoolean, useOutsideClick } from '@chakra-ui/react'
import React, { useMemo, useRef } from 'react'
import dndIcon from '~/assets/svgs/icon-dnd.svg?react'
import downChevron from '~/assets/svgs/icon-down-chevron-bold.svg?react'
import upChevron from '~/assets/svgs/icon-up-chevron-bold.svg?react'
import { DoNotDisturb, useSettingsState } from '~/atoms/SettingsState'
import {
  Menu,
  MenuPlacement,
  MenuPositioningWrapper,
} from '~/components/shared/Dropdown/Menu'
import { MenuItem } from '~/components/shared/Dropdown/MenuItem'

import {
  DoNotDisturbSplitButtonVariants,
  useDoNotDisturbSplitButtonStyle,
} from './componentStyles'
import { DoNotDisturbSplitButtonStyleValues } from './componentStyles/DoNotDisturbSplitButton.styles'

interface DoNotDisturbState {
  title: string
  subtitle?: string
  enabled: boolean
}

const getDoNotDisturbState = (
  doNotDisturbTimestamp: Date | null | undefined,
): DoNotDisturbState => {
  if (doNotDisturbTimestamp === undefined) {
    return {
      title: 'Receiving Calls',
      enabled: false,
    }
  }
  const state: DoNotDisturbState = {
    title: 'Do Not Disturb',
    enabled: true,
  }
  if (doNotDisturbTimestamp)
    state.subtitle = `Until ${new Date(
      doNotDisturbTimestamp,
    ).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`

  return state
}

const getDoNotDisturbVariant = (isDoNotDisturbEnabled: boolean) => {
  if (!isDoNotDisturbEnabled) {
    return undefined
  }
  return DoNotDisturbSplitButtonVariants.DoNotDisturb
}

export const DoNotDisturbSplitButton = () => {
  const {
    settings: {
      webPhone: { doNotDisturbTimestamp },
    },
    canUseWebPhone,
    setDoNotDisturb,
  } = useSettingsState()

  const [isMenuVisible, setIsMenuVisible] = useBoolean(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const doNotDisturbState = getDoNotDisturbState(doNotDisturbTimestamp)

  const variant = useMemo(() => {
    return getDoNotDisturbVariant(doNotDisturbState.enabled)
  }, [doNotDisturbState.enabled])

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      setIsMenuVisible.off()
    },
  })

  const handleLeftButtonClick = () => {
    if (doNotDisturbState.enabled) {
      setDoNotDisturb(undefined)
    } else {
      setDoNotDisturb(DoNotDisturb.INDEFINITE)
    }
  }

  const handleMenuItemClick = (value: number | undefined) => {
    setDoNotDisturb(value)
    setIsMenuVisible.off()
  }

  const menuItems: MenuItem<number | undefined>[] = [
    {
      content: 'Do not disturb',
      value: DoNotDisturb.INDEFINITE,
    },
    {
      content: 'Do not disturb for 30 minutes',
      value: DoNotDisturb.HALF_HOUR,
    },
    {
      content: 'Do not disturb for 1 hour',
      value: DoNotDisturb.ONE_HOUR,
    },
    {
      content: 'Do not disturb for 2 hours',
      value: DoNotDisturb.TWO_HOURS,
    },
    {
      content: 'Do not disturb until tomorrow',
      value: DoNotDisturb.TOMORROW,
    },
  ]

  if (doNotDisturbState.enabled) {
    menuItems.unshift({
      content: 'Turn off do not disturb',
      value: undefined,
    })
  }

  const styles = useDoNotDisturbSplitButtonStyle({ variant: variant })
  return !canUseWebPhone ? (
    <></>
  ) : (
    <Flex sx={styles.container}>
      <Flex sx={styles.leftButton} onClick={handleLeftButtonClick}>
        <Icon sx={styles.doNotDisturbIcon} as={dndIcon} />
        <Flex sx={styles.leftButtonTextWrapper}>
          <Text sx={styles.titleText}>
            {doNotDisturbState.enabled ? 'Do Not Disturb' : 'Receiving Calls'}
          </Text>
          {doNotDisturbState.subtitle && (
            <Text sx={styles.subtitleText}>{doNotDisturbState.subtitle}</Text>
          )}
        </Flex>
      </Flex>
      <MenuPositioningWrapper ref={containerRef}>
        <Flex
          ref={buttonRef}
          sx={styles.rightButton}
          onClick={() => setIsMenuVisible.toggle()}
        >
          <Icon
            sx={styles.chevronIcon}
            as={isMenuVisible ? upChevron : downChevron}
          />
        </Flex>
        {isMenuVisible && (
          <Menu
            anchorRef={buttonRef}
            items={menuItems}
            onSelect={handleMenuItemClick}
            options={{
              width: DoNotDisturbSplitButtonStyleValues.menuWidth,
              placement: MenuPlacement.BottomRight,
            }}
          />
        )}
      </MenuPositioningWrapper>
    </Flex>
  )
}
